import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { BringmeProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type BringmeFormValues = BringmeProjectConfigurationDto;

export function BringmePage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();

  const { data, isLoading, error } = useQuery(integrationQueries.bringmeConfiguration());
  const mutation = integrationMutations.useEditBringmeConfiguration();

  if (!hasPermission((x) => x.integrations.canManageBringme)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.bringme.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <BringmeForm
        defaultValues={{ enabled: data?.enabled ?? false, locationId: data?.locationId ?? "" }}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

interface BringmeFormProps {
  defaultValues: Partial<BringmeFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: BringmeFormValues) => void;
}

export function BringmeForm({ defaultValues, isSubmitting, onSubmit }: BringmeFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<BringmeFormValues>({ defaultValues });
  const enabled = useWatch({ control: formMethods.control, name: "enabled" });

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.bringme.title")}>
        <Form.Body>
          <FormCheckbox<BringmeFormValues, "enabled"> name="enabled" label={t("common.action.enable")} />
          <FormField label={t("page.integration.bringme.form.location-id")} required={enabled}>
            <FormInput<BringmeFormValues, "locationId">
              name="locationId"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.bringme.form.location-id")
                    : () => undefined,
                },
              }}
              disabled={!enabled}
            />
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
